import React, { useMemo } from 'react'
import {
  useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import styled from '@emotion/styled'

import { RowContext } from '../Context/RowContext'

export interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string | number,
  'data-row-index'?: number,
  'data-fixed-item-count'?: number
}

export const Row = (props: RowProps) => {
  const rowIndex = props['data-row-index'] || 0
  const fixedRowCount = props['data-fixed-item-count'] || 0
  const isFixed = rowIndex < fixedRowCount

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: props['data-row-key'],
    disabled: isFixed
  })

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative' as const, zIndex: 9999 } : {})
  }

  const contextValue = useMemo(
    () => ({
      setActivatorNodeRef: isFixed ? undefined : setActivatorNodeRef,
      listeners: isFixed ? {} : listeners,
      isFixed
    }),
    [setActivatorNodeRef, listeners, isFixed]
  )

  return (
    <RowContext.Provider value={contextValue}>
      <RowElement
        {...props}
        ref={setNodeRef}
        style={style}
        {...(isFixed ? {} : attributes)}
        isDragging={isDragging}
      />
    </RowContext.Provider>
  )
}

const RowElement = styled.tr<{ isDragging: boolean }>(({ theme, isDragging }) => `
  ${isDragging
    ? `
      background: ${theme.colors.primaryBackground};
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      border-radius: ${theme.radius.medium};
      border: 1px solid ${theme.colors.border};
    `
    : ''}
`)
